import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { catalogStats, listProducts, myOrders, productsByIds, similarProducts } from "../api/endpoints";
import { usePhotoSrc } from "../components/ProductPhoto";
import ProductPhoto from "../components/ProductPhoto";
import ProductScroller from "../components/ProductScroller";
import Countdown from "../components/Countdown";
import { ReservationSheet } from "../components/Voucher";
import SizeIntro from "../components/SizeIntro";
import { HandEllipse } from "../components/Hand";
import { getRecentlyViewedIds } from "../lib/recentlyViewed";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useUserStore } from "../store/user";
import { useLikesStore } from "../store/likes";
import { haptic } from "../lib/tg";
import type { CatalogStats, CategoryStat, Order, Product } from "../types";
import { categoryLabel } from "../types";

// Сколько товаров тянуть в одну горизонтальную ленту.
const LANE_SIZE = 12;

// Склонение существительного по числу: 1 вещь / 2 вещи / 5 вещей.
function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/**
 * Активная бронь на витрине: вещь отложена и срок идёт.
 *
 * Талон лежит и в профиле, но бронь — единственное на экране, у чего тикает
 * таймер: ради неё возвращаются в приложение, и прятать её во вторую вкладку
 * профиля неправильно.
 */
function ActiveHold({ order }: { order: Order }) {
  const [talonOpen, setTalonOpen] = useState(false);
  const p = order.product;

  return (
    <section className="hold">
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <ProductPhoto productId={p.id} alt={p.title ?? p.sku} size={56} variant="thumb" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="section-label" style={{ margin: 0 }}>Ваша бронь</p>
          <p style={{
            margin: "3px 0 2px", fontWeight: 600, fontSize: 14,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {p.title ?? categoryLabel(p.category)}
          </p>
          <p style={{ margin: 0, fontSize: 13 }}>
            {order.expires_at && <Countdown expiresAt={order.expires_at} />}
          </p>
        </div>
        <button
          className="btn btn--secondary"
          style={{ width: "auto", padding: "8px 14px", fontSize: 13, flexShrink: 0 }}
          onClick={() => { haptic.tap(); setTalonOpen(true); }}
        >
          Талон
        </button>
      </div>
      {talonOpen && <ReservationSheet order={order} onClose={() => setTalonOpen(false)} />}
    </section>
  );
}

/** Плитка категории: обложка — самый свежий товар, поверх — название и счётчик. */
function CategoryTile({ stat }: { stat: CategoryStat }) {
  const src = usePhotoSrc(stat.cover_id, 0, "thumb");
  return (
    <Link to={`/catalog?category=${encodeURIComponent(stat.category)}`} className="tile">
      {src && <img src={src} alt="" className="tile__img" />}
      <div className="tile__label">
        <span className="tile__name">{categoryLabel(stat.category)}</span>
        <span className="micro tile__count">{stat.count}</span>
      </div>
    </Link>
  );
}

export default function HomePage() {
  useDocumentTitle();   // на витрине заголовок вкладки = одно название магазина
  const { user } = useUserStore();
  const { ids: likedIds, load: loadLikes } = useLikesStore();
  const navigate = useNavigate();

  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [fresh, setFresh] = useState<Product[]>([]);
  const [mine, setMine] = useState<Product[]>([]);
  const [recent, setRecent] = useState<Product[]>([]);
  const [forYou, setForYou] = useState<Product[]>([]);
  const [picks, setPicks] = useState<Product[]>([]);
  const [freshLoaded, setFreshLoaded] = useState(false);
  const [holds, setHolds] = useState<Order[]>([]);

  const hasSizePref = !!(user?.clothing_size || user?.shoe_size);
  const firstName = user?.first_name?.trim() || null;

  useEffect(() => { loadLikes(); }, [loadLikes]);

  // Сводка и новинки — два запроса, которые нужны витрине всегда.
  // Ошибки глушим: пустая лента просто не рисуется, экран не должен падать целиком.
  useEffect(() => {
    catalogStats().then(setStats).catch(() => {});
    listProducts({ new_only: true, limit: LANE_SIZE })
      .then(setFresh)
      .catch(() => {})
      .finally(() => setFreshLoaded(true));
  }, []);

  // Лента «ваш размер» — только когда в профиле есть размеры (иначе сервер вернёт пусто).
  useEffect(() => {
    if (!hasSizePref) { setMine([]); return; }
    let cancelled = false;
    listProducts({ my_size: true, limit: LANE_SIZE })
      .then((list) => { if (!cancelled) setMine(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [hasSizePref, user?.clothing_size, user?.shoe_size]);

  // Активные брони клиента. Истёкшие по времени, но ещё не подчищенные планировщиком,
  // отсеиваем на месте: показывать таймер, который уже в минусе, — обман.
  useEffect(() => {
    myOrders()
      .then((list) => setHolds(list.filter(
        (o) => o.type === "reservation"
          && o.status === "pending"
          && (!o.expires_at || new Date(o.expires_at).getTime() > Date.now()),
      )))
      .catch(() => {});
  }, []);

  // «Вы смотрели» — из localStorage, порядок выстраиваем сами (сервер отдаёт как попало).
  useEffect(() => {
    const ids = getRecentlyViewedIds().slice(0, LANE_SIZE);
    if (!ids.length) return;
    productsByIds(ids)
      .then((list) => {
        const byId = new Map(list.map((p) => [p.id, p]));
        setRecent(ids.map((id) => byId.get(id)).filter((p): p is Product => !!p));
      })
      .catch(() => {});
  }, []);

  // «Вам понравится» — похожие на последний лайк. Лайков нет — блока нет.
  useEffect(() => {
    const last = [...likedIds].pop();
    if (!last) { setForYou([]); return; }
    similarProducts(last)
      .then((list) => setForYou(list.slice(0, LANE_SIZE)))
      .catch(() => {});
  }, [likedIds]);

  // Витрина не должна состоять из одних плиток. Если новинок нет, а личных лент
  // ещё не из чего собрать (новый посетитель), показываем подборку из наличия.
  // Seed — сегодняшняя дата: порядок стабилен в течение дня и меняется назавтра.
  const lanesEmpty = freshLoaded && !fresh.length && !mine.length && !recent.length && !forYou.length;

  useEffect(() => {
    if (!lanesEmpty) { setPicks([]); return; }
    // Флаг обязателен: пока запрос летит, пользователь может выбрать размер в
    // карточке-знакомстве — тогда личная лента уже есть, и ответ, пришедший
    // следом, воскрешал бы запасную ленту поверх решения её спрятать.
    let cancelled = false;
    listProducts({ seed: new Date().toISOString().slice(0, 10), limit: LANE_SIZE })
      .then((list) => { if (!cancelled) setPicks(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lanesEmpty]);

  // Подзаголовок витрины: наличие + новинки. До загрузки сводки строки нет —
  // мигать плейсхолдером на одной строке хуже, чем показать её на 200 мс позже.
  const summary = stats
    ? [
        `${stats.available} ${plural(stats.available, "вещь", "вещи", "вещей")} в наличии`,
        stats.new_week > 0
          ? `${stats.new_week} ${plural(stats.new_week, "новинка", "новинки", "новинок")} за неделю`
          : null,
      ].filter(Boolean).join(" · ")
    : null;

  const mySizeLabel = [user?.clothing_size, user?.shoe_size && `обувь ${user.shoe_size}`]
    .filter(Boolean).join(" · ");

  return (
    <div className="container home">

      {/* Шапка витрины. Единственный рисованный жест экрана — обводка на имени
          магазина; ниже по странице руки быть не должно. */}
      <header className="home__hero">
        <h1 style={{ margin: 0 }}>
          {firstName ? (
            <>
              {firstName}, <span className="hand-wrap">
                заходите
                <HandEllipse className="hand-ellipse" />
              </span>
            </>
          ) : (
            <span className="hand-wrap">
              Storosilov
              <HandEllipse className="hand-ellipse" />
            </span>
          )}
        </h1>
        {summary && <p className="micro home__summary">{summary}</p>}
      </header>

      {/* Бронь идёт первой: у неё срок, всё остальное на экране может подождать. */}
      {holds.map((o) => <ActiveHold key={o.id} order={o} />)}

      {/* Знакомство: без размеров в профиле персональных лент не будет, поэтому
          карточка стоит первой. Заполнен профиль — на её месте лента «ваш размер». */}
      {!hasSizePref && <SizeIntro />}

      {hasSizePref && mine.length > 0 && (
        <>
          <ProductScroller title={`Ваш размер · ${mySizeLabel}`} products={mine} />
          {stats && stats.my_size > mine.length && (
            <button
              className="btn btn--secondary home__more"
              onClick={() => navigate("/catalog?my=1")}
            >
              Всё моего размера ({stats.my_size})
            </button>
          )}
        </>
      )}

      {fresh.length > 0 && (
        <>
          <ProductScroller title="✨ Только что приехало" products={fresh} />
          <button
            className="btn btn--secondary home__more"
            onClick={() => navigate("/catalog?status=new")}
          >
            Все новинки
          </button>
        </>
      )}

      {picks.length > 0 && <ProductScroller title="Из наличия" products={picks} />}
      {recent.length > 0 && <ProductScroller title="Вы смотрели" products={recent} />}
      {forYou.length > 0 && <ProductScroller title="Вам понравится" products={forYou} />}

      {/* Категории плитками вместо ряда из 23 табов: выбор глазами, а не чтением. */}
      {stats && stats.categories.length > 0 && (
        <section style={{ marginTop: 24 }}>
          <h3 className="section-label">Категории</h3>
          <div className="tiles">
            {stats.categories.map((c) => <CategoryTile key={c.category} stat={c} />)}
          </div>
          <Link to="/catalog" className="btn btn--secondary home__more" style={{ marginTop: 12 }}>
            Весь каталог
          </Link>
        </section>
      )}

      {/* Выход для тех, кто не нашёл своё: подписка вместо ухода без покупки. */}
      <section className="home__subscribe">
        <p className="section-label" style={{ margin: 0 }}>Не нашли своё?</p>
        <p style={{ margin: "6px 0 12px", fontSize: 14, color: "var(--hint)" }}>
          Подпишитесь на категорию и размер — бот напишет, когда такое приедет.
        </p>
        <Link to="/subscriptions" className="btn btn--secondary" style={{ width: "auto", display: "inline-block" }}>
          🔔 Настроить подписку
        </Link>
      </section>
    </div>
  );
}
